import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useCategories } from '@/context/CategoriesContext';
import { PublicHeader } from '@/components/marketplace/PublicHeader';

import { AdCard } from '@/components/marketplace/AdCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SlidersHorizontal, X } from 'lucide-react';

export default function SearchPage() {
    const [params, setParams] = useSearchParams();
    const [ads, setAds] = useState([]);
    const [total, setTotal] = useState(0);
    const cats = useCategories();
    const [loading, setLoading] = useState(true);

    const q = params.get('q') || '';
    const category = params.get('category') || '';
    const condition = params.get('condition') || '';
    const sort = params.get('sort') || 'recent';
    const min_price = params.get('min_price') || '';
    const max_price = params.get('max_price') || '';

    useEffect(() => {
        setLoading(true);
        api.get('/ads', { params: { q, category, condition, sort, min_price: min_price || undefined, max_price: max_price || undefined, limit: 48 } })
           .then(r => { setAds(r.data.items); setTotal(r.data.total); })
           .finally(() => setLoading(false));
    }, [q, category, condition, sort, min_price, max_price]);

    const update = (k, v) => {
        const next = new URLSearchParams(params);
        if (v) next.set(k, v); else next.delete(k);
        setParams(next);
    };

    const clear = () => setParams(new URLSearchParams(q ? { q } : {}));

    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />
            <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight">{q ? `Resultados para: "${q}"` : 'Todos los anuncios'}</h1>
                        <p className="text-sm text-muted-foreground" data-testid="search-total">{total} resultados</p>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6">
                    <aside className="col-span-12 lg:col-span-3">
                        <div className="rounded-2xl border bg-card p-4 space-y-4 sticky top-20">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm font-medium"><SlidersHorizontal className="h-4 w-4" /> Filtros</div>
                                <Button variant="ghost" size="sm" onClick={clear}><X className="h-3 w-3 mr-1" />Limpiar</Button>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Categoría</label>
                                <Select value={category || 'all'} onValueChange={(v) => update('category', v === 'all' ? '' : v)}>
                                    <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Todas" /></SelectTrigger>
                                    <SelectContent><SelectItem value="all">Todas</SelectItem>{cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Condición</label>
                                <Select value={condition || 'all'} onValueChange={(v) => update('condition', v === 'all' ? '' : v)}>
                                    <SelectTrigger data-testid="filter-condition"><SelectValue placeholder="Todas" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        <SelectItem value="nuevo">Nuevo</SelectItem>
                                        <SelectItem value="seminuevo">Seminuevo</SelectItem>
                                        <SelectItem value="usado">Usado</SelectItem>
                                        <SelectItem value="para_partes">Para partes</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Precio</label>
                                <div className="flex gap-2 mt-1">
                                    <Input type="number" placeholder="Mín" value={min_price} onChange={(e) => update('min_price', e.target.value)} data-testid="filter-min-price" />
                                    <Input type="number" placeholder="Máx" value={max_price} onChange={(e) => update('max_price', e.target.value)} data-testid="filter-max-price" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Ordenar</label>
                                <Select value={sort} onValueChange={(v) => update('sort', v)}>
                                    <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="recent">Más recientes</SelectItem>
                                        <SelectItem value="price_asc">Precio ascendente</SelectItem>
                                        <SelectItem value="price_desc">Precio descendente</SelectItem>
                                        <SelectItem value="popular">Más vistos</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </aside>
                    <section className="col-span-12 lg:col-span-9">
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
                                {[...Array(9)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
                            </div>
                        ) : ads.length === 0 ? (
                            <div className="text-center py-24 text-muted-foreground">Sin resultados para tu búsqueda.</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5" data-testid="search-results-grid">
                                {ads.map(a => <AdCard key={a.id} ad={a} />)}
                            </div>
                        )}
                    </section>
                </div>
            </main>
            
        </div>
    );
}
